<template>
	<v-form @keydown.enter.prevent>
		<v-container v-if="isEdit">
			<v-layout row wrap>
				<v-flex xs12>
					<v-text-field
						ref="defaultFocusElement"
						v-model="value.addressLine1"
						label="Address Line 1"
						:error-messages="addressErrors"
					></v-text-field>
				</v-flex>
				<v-flex xs12>
					<v-text-field v-model="value.addressLine2" label="Address Line 2"></v-text-field>
				</v-flex>
				<v-flex xs12>
					<v-autocomplete v-model="value.country" :items="countries" label="Country"></v-autocomplete>
				</v-flex>
				<v-flex xs12 md5>
					<v-text-field v-model="value.suburb" :label="isAustralia ? 'Suburb' : 'City/Suburb'"></v-text-field>
				</v-flex>
				<v-flex xs12 md4>
					<v-select
						v-if="isAustralia"
						v-model="value.state"
						:items="states"
						label="State/Territory"
						:error-messages="stateErrors"
					></v-select>
					<v-text-field v-else v-model="value.state" label="State/Province/Region"></v-text-field>
				</v-flex>
				<v-flex xs12 md3>
					<v-text-field
						v-model="value.postcode"
						:label="isAustralia ? 'Postcode' : 'Postal Code'"
						:maxlength="isAustralia ? 4 : null"
						:error-messages="postcodeErrors"
					></v-text-field>
				</v-flex>
				<v-flex xs12 md6>
					<v-menu
						ref="effectiveDateMenu"
						v-model="effectiveDateMenu"
						:close-on-content-click="false"
						:nudge-right="40"
						:return-value.sync="value.effectiveDate"
						lazy
						transition="scale-transition"
						offset-y
						full-width
						min-width="290px"
					>
						<v-text-field
							slot="activator"
							:value="effectiveDateFormatted"
							label="Effective Date"
							:error-messages="effectiveDateErrors"
							readonly
						></v-text-field>
						<v-date-picker v-model="value.effectiveDate" @input="$refs.effectiveDateMenu.save(value.effectiveDate)"></v-date-picker>
					</v-menu>
				</v-flex>
			</v-layout>
		</v-container>

		<v-container v-else-if="allowEmpty && !hasAddressValue">
			<div class="grey--text text--darken-1">No residential address entered.</div>
		</v-container>

		<v-container v-else>
			<v-layout row wrap>
				<v-flex xs12 md6>
					<div class="form-group">
						<span class="form-label">Residential Address</span>
						<span class="form-value address-lines">{{ formattedAddress || '-' }}</span>
					</div>
				</v-flex>
				<v-flex xs12 md6>
					<div class="form-group">
						<span class="form-label">Effective Date</span>
						<span class="form-value">{{ effectiveDateFormatted || '-' }}</span>
					</div>
				</v-flex>
			</v-layout>
		</v-container>
	</v-form>
</template>

<script>
	import { validationMixin } from 'vuelidate';
	import formMixin from '@/mixins/employeeFormMixin';
	import countries from '@/common/countries';

	export default {
		mixins: [validationMixin, formMixin],
		props: {
			allowEmpty: {
				type: Boolean,
				default: false
			},
			excludedEffectiveDates: {
				type: Array,
				default: () => []
			}
		},
		data() {
			return {
				effectiveDateMenu: false,
				countries,
				states: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA']
			};
		},
		validations() {
			return {
				value: {
					effectiveDate: {
						required: date => (this.allowEmpty && !this.hasAddressValue) || !!date,
						available: date => !date || !this.excludedEffectiveDates.includes(date.substring(0, 10))
					},
					addressLine1: {
						addressPresent: () => this.allowEmpty || this.hasAddressValue
					},
					addressLine2: {},
					suburb: {},
					state: {
						validState: state => !this.isAustralia || !state || this.states.includes(state.trim().toUpperCase())
					},
					postcode: {
						validPostcode: postcode => !this.isAustralia || !postcode || /^\d{4}$/.test(postcode.trim())
					},
					country: {}
				}
			};
		},
		computed: {
			isAustralia() {
				return !this.value.country || this.value.country.trim().toLowerCase() === 'australia';
			},
			hasAddressValue() {
				return ['addressLine1', 'addressLine2', 'suburb', 'state', 'postcode'].some(key => {
					const field = this.value[key];
					return field != null && String(field).trim() !== '';
				});
			},
			effectiveDateFormatted() {
				return this.value.effectiveDate ? this.$options.filters.formatDate(this.value.effectiveDate) : '';
			},
			formattedAddress() {
				return [
					this.value.addressLine1,
					this.value.addressLine2,
					[this.value.suburb, this.value.state, this.value.postcode].filter(Boolean).join(' '),
					this.value.country || 'Australia'
				]
					.filter(Boolean)
					.join('\n');
			},
			effectiveDateErrors() {
				const errors = [];
				if (!this.$v.value.effectiveDate.$dirty) return errors;
				!this.$v.value.effectiveDate.required && errors.push('Effective date is required.');
				!this.$v.value.effectiveDate.available && errors.push('An address already exists for this effective date.');
				return errors;
			},
			addressErrors() {
				if (!this.$v.value.addressLine1.$dirty || this.$v.value.addressLine1.addressPresent) return [];
				return ['Enter at least one address field.'];
			},
			stateErrors() {
				if (!this.$v.value.state.$dirty || this.$v.value.state.validState) return [];
				return ['Select a valid Australian state or territory.'];
			},
			postcodeErrors() {
				if (!this.$v.value.postcode.$dirty || this.$v.value.postcode.validPostcode) return [];
				return ['Postcode must contain exactly four digits.'];
			}
		}
	};
</script>

<style scoped>
	.address-lines {
		white-space: pre-line;
	}
</style>
